import React, { useEffect, useState } from "react";
import "./ProjectCard.css";
import { calculateDistance } from "../../utils/geo";
import { useSelector } from "react-redux";
import GoogleMapView from "../GoogleMapView";
import ImagesModalCarousel from "../ImagesModalCarousel";
import { Document, Page, pdfjs } from "react-pdf";
import PdfModal from "../PdfModal";
import { getStatusLabel } from "../../utils/stringUtils";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/build/pdf.worker.min.mjs`;

const ProjectCard = ({
  project,
  isAdmin = false,   // ← mode admin
  idUserClient = null,
  onLike,
  onPass,
  onReject,          // ← bouton admin
  isReceivedLike = false,
  isAlreadyMatched = false,
  isLikeSent = false
}) => {

  const apiUrl = process.env.REACT_APP_API_URL;
  const { user } = useSelector((state) => state.auth);
  const usersOnline = useSelector((state) => state.online.usersOnline);

  const [realDistance, setRealDistance] = useState(null);
  const [duration, setDuration] = useState(null);
  const [modalIndex, setModalIndex] = useState(null);
  const [pdfUrl, setPdfUrl] = useState(null);

  const clientId = idUserClient;
  const isOnline = clientId ? usersOnline[clientId] === true : false;

  const openPdf = (file, categorie) => {
    setPdfUrl(`${apiUrl}/api/files/?categorie=${categorie}&file=${file}`);
  };

  const badge = (online) => ({
    display: "inline-flex",
    alignItems: "center",
    gap: "6px",
    background: online ? "#d4f8d4" : "#f0f0f0",
    color: online ? "#258f25" : "#555",
    border: `1px solid ${online ? "#42c442" : "#aaa"}`,
    padding: "4px 8px",
    borderRadius: "6px",
    fontSize: "13px",
    marginLeft: "10px",
  });

  // -------------------------
  // Distance pour prestataire
  // -------------------------
  useEffect(() => {
    if (isAdmin) return;       // ← admin → pas de distance
    if (
      !user?.profilPrestataire ||
      !project?.latitudeProjet ||
      !project?.longitudeProjet
    )
      return;

    const fetchDistance = async (originLat, originLng, destLat, destLng) => {
      try {
        const res = await fetch(
          `${apiUrl}/api/maps?originLat=${originLat}&originLng=${originLng}&destLat=${destLat}&destLng=${destLng}`
        );
        const data = await res.json();
        if (data) {
          setRealDistance(data.distance_text);
          setDuration(data.duration_text);
        }
      } catch (err) {
        console.error("Erreur récupération distance :", err);
      }
    };

    fetchDistance(
      user.profilPrestataire.latitudePrestataire,
      user.profilPrestataire.longitudePrestataire,
      project.latitudeProjet,
      project.longitudeProjet
    );
  }, [project, user, apiUrl, isAdmin]);
    
  return (
    <div className="project-card">

      {/* HEADER */}
      <div className="project-card-header">
        <h2>{project.titreProjetClient}</h2>

        {/* Badge online */}
        {clientId && (
          <span style={badge(isOnline)}>
            {isOnline ? "En ligne" : "Hors ligne"}
          </span>
        )}

        <div className="tags">
          <span className="tag sector">{project.secteurProjetClient}</span>
          <span className="tag remote">
            {project.isRemotePossible ? "Télétravail OK" : "Sur site"}
          </span>
          <span className="tag status">{getStatusLabel(project.statusProjetClient)}</span>
        </div>
      </div>

      {/* GRID */}
      <div className="project-grid">

        {/* LEFT SIDE */}
        <div className="col-left">

          <p className="description">{project.descriptionProjetClient}</p>

          <div className="info-list">
            <div><strong>Budget :</strong> {project.budgetProjetClient === "50000-1000000" ? "Plus de 50 000" : project.budgetProjetClient} €</div>
            <div><strong>Date limite :</strong> {project.dateLimiteProjetClient}</div>
            <div><strong>Localisation :</strong> {project.villeProjetClient} ({project.codePostalProjetClient})</div>

            {/* Distance uniquement prestataire */}
            {!isAdmin && user?.profilPrestataire && (
              <>
                <div>
                  <strong>Distance (vol) :</strong> 
                  {" "}
                  {calculateDistance(
                    project.latitudeProjet,
                    project.longitudeProjet,
                    user.profilPrestataire.latitudePrestataire,
                    user.profilPrestataire.longitudePrestataire
                  )} km
                </div>

                {realDistance && (
                  <div><strong>Distance route :</strong> {realDistance} — {duration}</div>
                )}
              </>
            )}
          </div>

          {/* BESOINS */}
          {project.besoinsProjetClient?.length > 0 && (
            <div className="projet-besoins">
              {project.besoinsProjetClient.map((besoin) => (
                <span key={besoin.idBesoinProjetClient} className="besoin-badge">
                  <img
                    src={`${apiUrl}/api/files/?categorie=metiers&file=${besoin.metier.iconMetier}`}
                    alt={besoin.metier.labelMetier}
                    className="besoin-icon"
                  />
                  {besoin.metier.labelMetier} (x{besoin.nbPersonnes})
                </span>
              ))}
            </div>
          )}
        </div>

        {/* RIGHT SIDE */}
        <div className="col-right">

          {/* Carte : admin origine = projet seulement */}
          {isAdmin ? (
            <GoogleMapView
              origin={{ lat: project.latitudeProjet, lng: project.longitudeProjet }}
              destination={null}
            />
          ) : (
            user?.profilPrestataire && (
              <GoogleMapView
                origin={{
                  lat: user.profilPrestataire.latitudePrestataire,
                  lng: user.profilPrestataire.longitudePrestataire
                }}
                destination={{
                  lat: project.latitudeProjet,
                  lng: project.longitudeProjet
                }}
              />
            )
          )}
        </div>
      </div>

      {/* PHOTOS */}
      <div className="section">
        <h4>📷 Photos du projet</h4>

        {project.photosProjetClient?.length > 0 ? (
          <div className="photo-gallery">
            {project.photosProjetClient.map((photo, index) => (
              <img
                key={index}
                src={`${apiUrl}/api/files/?categorie=projets&file=${photo.fileNameFileProjetClient}`}
                className="photo-thumb"
                onClick={() => setModalIndex(index)}
                alt=""
              />
            ))}
          </div>
        ) : (
          <p style={{ opacity: 0.7 }}>Aucune photo fournie.</p>
        )}
      </div>

      {/* DOCUMENTS */}
      <div className="section">
        <h4>📎 Documents</h4>

        {project.docsProjetClient?.length > 0 ? (
          project.docsProjetClient.map((doc) => (
            <div
              key={doc.idFileProjetClient}
              className="pdf-thumbnail"
              onClick={() => openPdf(doc.fileNameFileProjetClient, "projets")}
            >
              <Document file={`${apiUrl}/api/files/?categorie=projets&file=${doc.fileNameFileProjetClient}`}>
                <Page pageNumber={1} width={120} />
              </Document>
            </div>
          ))
        ) : (
          <p style={{ opacity: 0.7 }}>Aucun document attaché.</p>
        )}
      </div>

      {/* ACTIONS */}
      {isAdmin ? (
        <div className="actions-row">
          <button className="btn-primary" onClick={() => onReject(project.idProjetClient)}>
            ❌ Refuser le projet
          </button>
        </div>
      ) : (
        !isAlreadyMatched &&
        !isLikeSent &&
        user?.profilPrestataire && (
          <div className="actions-row">
            {!isReceivedLike ? (
              <>
                <button className="btn btn-like" onClick={() => onLike(project.idProjetClient)}>👍 Liker</button>
              </>
            ) : (
              <>
                <button className="btn btn-pass" onClick={() => onPass(project.idProjetClient)}>❌ Refuser</button>
                <button className="btn btn-like" onClick={() => onLike(project.idProjetClient)}>💌 Confirmer</button>
              </>
            )}
          </div>
        )
      )}

      {/* MODALS */}
      {modalIndex !== null && (
        <ImagesModalCarousel
          images={project.photosProjetClient}
          startIndex={modalIndex}
          onClose={() => setModalIndex(null)}
        />
      )}

      {pdfUrl && <PdfModal fileUrl={pdfUrl} onClose={() => setPdfUrl(null)} />}

    </div>
  );
};

export default ProjectCard;
